<!-- A dialog and extra scaffolding to allow replacing images.

The image replacements are meant to be for either TVTemplates or
EditableTVSpotTemplates, but in general are not restricted to this. This
component just provides a hidden input to store the User-chosen image
file and a dialog to crop it to the original dimensions as specified by
the overlayData IMAGE field passed in via onImageChangeRequested().

The uploading should be done outside of this component, triggered by the
@cropped signal that is emitted from here.

Note: This has originally been written to replace images for
  OverlayDataField of kind IMAGE, and is based on a certain JSON object
  structure (a 'field'). This can be mocked though to use this widget in
  other contexts, e.g. a Branding logo.

## How to use

Include this component and use it via $refs:

  <image-replacement
    ref="imagereplacement"
    @cropped="onImageChangeConfirmed"
  />

  this.$refs.imagereplacement.onImageChangeRequested(opts);

-->
<template>
  <div>
    <!-- Hidden input to allow for browsing image files to upload -->
    <input
      ref="imageuploadinput"
      :key="'input-' + hiddenFileInputRenderKey"
      type="file"
      accept="image/png,image/jpeg,image/tiff"
      hidden
      @change="onReplacementImageFileChosen($event.target.files[0])"
    >
    <!-- Image Crop Dialog -->
    <image-crop-dialog
      v-if="imageCropping.width && imageCropping.height"
      :crop-ratio="cropRatio"
      :image-filename="imageCropping.filename"
      :image-filepath="imageCropping.filepath"
      :image-height="imageCropping.height"
      :image-width="imageCropping.width"
      :initial-background-color="'transparent'"
      :show.sync="imageCropping.showDialog"
      :view-mode="viewMode"
      @aborted="resetImageCropping()"
      @saved="onCroppedImageSaved"
    />
  </div>
</template>

<script>
import ImageCropDialog from '@/components/ImageCropDialog';

const defaults = {
  hiddenFileInputRenderKey: 0,
  imageCropping: {
    field: {
      /* aka overlay_data IMAGE Field with .data.width, .data.height and .value */
    },
    showDialog: false,
    filename: '',
    filepath: '',
    width: null,
    height: null,
    ratio: null,
  },
};

export default {
  components: {
    ImageCropDialog,
  },
  props: {
    /**
     * The view mode of the cropperjs instance.
     *
     * One of: 0, 1, 2, 3
     *
     * https://github.com/fengyuanchen/cropperjs/blob/main/README.md#viewmode
     */
    viewMode: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      hiddenFileInputRenderKey: defaults.hiddenFileInputRenderKey,
      imageCropping: {
        field: defaults.imageCropping.field,
        filename: defaults.imageCropping.filename,
        filepath: defaults.imageCropping.filepath,
        height: defaults.imageCropping.height,
        ratio: defaults.imageCropping.ratio,
        showDialog: defaults.imageCropping.showDialog,
        width: defaults.imageCropping.width,
      },
    };
  },
  computed: {
    /**
     * Pass custom ratio from overlay_options like 'any', if specified.
     */
    cropRatio() {
      if (
        this.imageCropping.field &&
        this.imageCropping.field.data &&
        this.imageCropping.field.data.crop_ratio
      ) {
        return this.imageCropping.field.data.crop_ratio;
      }
      return this.imageCropping.ratio;
    },
  },
  methods: {
    resetHiddenFileInput() {
      this.hiddenFileInputRenderKey += 1;
    },
    resetImageCropping(resetInput = true) {
      this.imageCropping = {
        field: {},
        filename: defaults.imageCropping.filename,
        filepath: defaults.imageCropping.filepath,
        height: defaults.imageCropping.height,
        ratio: defaults.imageCropping.ratio,
        showDialog: defaults.imageCropping.showDialog,
        width: defaults.imageCropping.width,
      };
      if (resetInput) {
        this.resetHiddenFileInput();
      }
    },
    /**
     * User clicked an IMAGE overlay data field to change it.
     *
     * Args:
     *
     *   opts (Object):
     *     .field (Object)
     *       .name (String)
     *       .data (Object)
     *         .width (Number)
     *         .height (Number)
     *         .crop_ratio (String): Optional.
     *
     *     .value (String): Optional. Current image URL.
     *
     *     .recrop (Boolean): Optional, false by default. When this is
     *        true, will open the current image in a cropping dialog
     *        instead of letting the user pick a new image file.
     *
     * This function populates this.imageCropping as a side effect.
     */
    onImageChangeRequested(opts) {
      // Safari on MacOS/iOS has problems with issuing a programmatic
      // .click() event and it has problems with using a re-rendered ref
      // within a $nextTick() (when we call resetHiddenFileInput()): We
      // avoid a forced re-rendering of the input here for that reason
      // when using Safari/iOS.
      const usingSafari =
        this.$store.state.common.runtimeEnv &&
        (this.$store.state.common.runtimeEnv.safari ||
          this.$store.state.common.runtimeEnv.iOS);
      const resetInput = !usingSafari;
      this.resetImageCropping(resetInput);

      this.imageCropping.field = opts.field;

      if (opts.filename) {
        this.imageCropping.filename = opts.filename;
      }

      if (opts.recrop) {
        const imageUrl = opts.value;
        this.recropImage(imageUrl);
      } else {
        this.replaceImage();
      }
    },
    recropImage(imageUrl) {
      this.onRecroppingCurrentImageByUrl(imageUrl);
    },
    replaceImage() {
      this.$refs.imageuploadinput.click();
    },
    /**
     * User has chosen an image file, let him crop it in the dialog.
     *
     * Args:
     *
     *   file (File|Blob)
     */
    onReplacementImageFileChosen(file) {
      if (!file) {
        return;
      }

      const vm = this;

      function openCroppingDialog(imageUrl, fileName) {
        vm.onImageUrlChanged(imageUrl, fileName);
      }

      const isProbablyTif =
        file.name.endsWith('.tif') || file.name.endsWith('.tiff');
      if (isProbablyTif) {
        console.log('[INFO] Converting .tif to .png first...');
        this.$api.convertTifToPng(file, file.name).then((response) => {
          openCroppingDialog(response.data.image_url, file.name);
        });
      } else {
        openCroppingDialog(URL.createObjectURL(file), file.name);
      }
    },
    onRecroppingCurrentImageByUrl(imageUrl) {
      if (!imageUrl) {
        return;
      }
      const filename = this.imageCropping.filename || imageUrl.split('/').pop();
      this.onImageUrlChanged(imageUrl, filename);
    },
    onImageUrlChanged(imageUrl, filename) {
      const img = new Image();
      img.onload = () => {
        if (
          !this.imageCropping.field ||
          !this.imageCropping.field.data ||
          !this.imageCropping.field.data.width ||
          !this.imageCropping.field.data.height
        ) {
          throw (
            'Error replacing image: IMAGE ' +
            'field expected to have width and height attributes, ' +
            'but has not: ' +
            JSON.stringify(this.imageCropping, null, 2)
          );
        }
        this.imageCropping.ratio =
          this.imageCropping.field.data.width /
          this.imageCropping.field.data.height;
        this.imageCropping.width = img.naturalWidth;
        this.imageCropping.height = img.naturalHeight;
        this.imageCropping.filepath = imageUrl;
        this.imageCropping.filename = filename;
        this.imageCropping.showDialog = true;
      };
      img.src = imageUrl;
    },
    onCroppedImageSaved(file) {
      const payload = {
        file: file,
        fieldName: this.imageCropping.field.name,
        originalFilename: this.imageCropping.filename,
      };
      this.$emit('cropped', payload);

      // Reset at the end to allow for another cropping and upload.
      this.resetImageCropping();
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/ImageReplacement.vue